import {useState, useMemo, useCallback, useRef} from 'react'
import {
    Box,
    IconButton,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Autocomplete,
    Popper
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CodeIcon from '@mui/icons-material/Code'
import ViewListIcon from '@mui/icons-material/ViewList'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import Editor from '@monaco-editor/react'

function sortKeys(obj: any): any {
    if (Array.isArray(obj)) return obj.map(sortKeys)
    if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).sort().reduce((acc: any, key: string) => {
            acc[key] = sortKeys(obj[key])
            return acc
        }, {})
    }
    return obj
}

function formatCellValue(value: any): string {
    if (value === null) return 'null'
    if (value === undefined) return ''
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
}

interface Condition {
    field: string
    op: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'LIKE' | 'NOT LIKE' | 'IS NULL' | 'IS NOT NULL' | 'IN'
    value: string
}

interface ParsedWhere {
    conditions: Condition[]
    combinators: ('AND' | 'OR')[]
}

function parseWhereClause(input: string): ParsedWhere | null {
    const text = input.trim()
    if (!text) return null

    const conditions: Condition[] = []
    const combinators: ('AND' | 'OR')[] = []

    const condRegex = /([\w.]+)\s+(IS\s+NOT\s+NULL|IS\s+NULL|NOT\s+LIKE|LIKE|IN|!=|>=|<=|>|<|=)\s*(.*?)$/i
    const parts: string[] = []
    let remaining = text

    // Split by AND/OR while preserving them
    const splitRegex = /\s+(AND|OR)\s+/gi
    let match: RegExpExecArray | null
    let lastIndex = 0
    while ((match = splitRegex.exec(remaining)) !== null) {
        parts.push(remaining.substring(lastIndex, match.index).trim())
        combinators.push(match[1].toUpperCase() as 'AND' | 'OR')
        lastIndex = match.index + match[0].length
    }
    parts.push(remaining.substring(lastIndex).trim())

    for (const part of parts) {
        const m = condRegex.exec(part)
        if (!m) return null
        const field = m[1]
        const op = m[2].toUpperCase().replace(/\s+/g, ' ') as Condition['op']
        let value = m[3]?.trim() ?? ''
        // Strip surrounding quotes
        if ((value.startsWith("'") && value.endsWith("'")) || (value.startsWith('"') && value.endsWith('"'))) {
            value = value.slice(1, -1)
        }
        conditions.push({field, op, value})
    }

    return conditions.length > 0 ? {conditions, combinators} : null
}

function evaluateCondition(row: any, cond: Condition): boolean {
    const rawValue = row?.[cond.field]
    const cellStr = formatCellValue(rawValue)

    if (cond.op === 'IS NULL') return rawValue === null || rawValue === undefined
    if (cond.op === 'IS NOT NULL') return rawValue !== null && rawValue !== undefined

    if (cond.op === 'IN') {
        const vals = cond.value.replace(/^\(/, '').replace(/\)$/, '').split(',').map(v => {
            const t = v.trim()
            return (t.startsWith("'") && t.endsWith("'")) || (t.startsWith('"') && t.endsWith('"')) ? t.slice(1, -1) : t
        })
        return vals.some(v => cellStr.toLowerCase() === v.toLowerCase())
    }

    const condVal = cond.value
    const numRaw = Number(rawValue)
    const numCond = Number(condVal)
    const bothNumeric = !isNaN(numRaw) && !isNaN(numCond) && rawValue !== '' && rawValue !== null && rawValue !== undefined

    switch (cond.op) {
        case '=':
            return cellStr.toLowerCase() === condVal.toLowerCase()
        case '!=':
            return cellStr.toLowerCase() !== condVal.toLowerCase()
        case '>':
            return bothNumeric ? numRaw > numCond : cellStr > condVal
        case '<':
            return bothNumeric ? numRaw < numCond : cellStr < condVal
        case '>=':
            return bothNumeric ? numRaw >= numCond : cellStr >= condVal
        case '<=':
            return bothNumeric ? numRaw <= numCond : cellStr <= condVal
        case 'LIKE': {
            const pattern = condVal.toLowerCase().replace(/%/g, '.*').replace(/_/g, '.')
            return new RegExp(`^${pattern}$`, 'i').test(cellStr)
        }
        case 'NOT LIKE': {
            const pattern = condVal.toLowerCase().replace(/%/g, '.*').replace(/_/g, '.')
            return !new RegExp(`^${pattern}$`, 'i').test(cellStr)
        }
        default:
            return false
    }
}

function evaluateWhere(row: any, parsed: ParsedWhere): boolean {
    if (parsed.conditions.length === 0) return true
    let result = evaluateCondition(row, parsed.conditions[0])
    for (let i = 0; i < parsed.combinators.length; i++) {
        const next = evaluateCondition(row, parsed.conditions[i + 1])
        if (parsed.combinators[i] === 'AND') {
            result = result && next
        } else {
            result = result || next
        }
    }
    return result
}

interface ProposalJsonViewerProps {
    data: string
    rawData: any
}

function ProposalJsonViewer({data, rawData}: ProposalJsonViewerProps) {
    const [viewMode, setViewMode] = useState<'raw' | 'friendly'>('friendly')
    const [copied, setCopied] = useState(false)
    const [selectedDomain, setSelectedDomain] = useState<string | null>(null)
    const [search, setSearch] = useState('')
    const [sqlFilter, setSqlFilter] = useState('')
    const [filterError, setFilterError] = useState<string | null>(null)
    const [filterOptions, setFilterOptions] = useState<string[]>([])
    const [filterOpen, setFilterOpen] = useState(false)
    const filterInputRef = useRef<HTMLInputElement>(null)

    const domains = useMemo(() => {
        if (!rawData || typeof rawData !== 'object' || Array.isArray(rawData)) return []
        return Object.keys(rawData).sort()
    }, [rawData])

    // Auto-select first domain
    useMemo(() => {
        if (domains.length > 0 && !selectedDomain) {
            setSelectedDomain(domains[0])
        }
    }, [domains, selectedDomain])

    const domainData = useMemo(() => {
        if (!rawData || !selectedDomain || !rawData[selectedDomain]) return null
        return rawData[selectedDomain]
    }, [rawData, selectedDomain])

    const columns = useMemo(() => {
        if (!domainData) return []
        const colSet = new Set<string>()
        const items = Array.isArray(domainData) ? domainData : [domainData]
        for (const item of items) {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
                Object.keys(item).forEach(k => colSet.add(k))
            }
        }
        return Array.from(colSet).sort()
    }, [domainData])

    const OPERATORS = ['=', '!=', '>', '<', '>=', '<=', 'LIKE', 'NOT LIKE', 'IS NULL', 'IS NOT NULL', 'IN']
    const KEYWORDS = ['AND', 'OR']

    const updateFilterOptions = useCallback((value: string, cursorPos?: number) => {
        const pos = cursorPos ?? value.length
        const textBefore = value.substring(0, pos)

        // Find the current token being typed
        const lastSpace = textBefore.lastIndexOf(' ')
        const currentToken = textBefore.substring(lastSpace + 1).toLowerCase()

        // Determine context: what comes before the current token
        const beforeToken = textBefore.substring(0, lastSpace + 1).trimEnd()
        const lastWord = beforeToken.split(/\s+/).pop()?.toUpperCase() || ''

        // After AND/OR or at start -> suggest field names
        if (!beforeToken || lastWord === 'AND' || lastWord === 'OR') {
            const matches = currentToken
                ? columns.filter(c => c.toLowerCase().startsWith(currentToken))
                : columns
            setFilterOptions(matches)
            return
        }

        // After a field name -> suggest operators
        if (columns.some(c => c.toLowerCase() === lastWord.toLowerCase())) {
            const matches = currentToken
                ? OPERATORS.filter(o => o.toLowerCase().startsWith(currentToken))
                : OPERATORS
            setFilterOptions(matches)
            return
        }

        // After a complete condition (field op value) -> suggest AND/OR
        const condPattern = /(?:IS\s+NOT\s+NULL|IS\s+NULL|NOT\s+LIKE\s+\S+|LIKE\s+\S+|IN\s+\([^)]*\)|[!=><]+\s*\S+)$/i
        if (condPattern.test(beforeToken) || condPattern.test(textBefore)) {
            const matches = currentToken
                ? KEYWORDS.filter(k => k.toLowerCase().startsWith(currentToken))
                : KEYWORDS
            setFilterOptions(matches)
            return
        }

        setFilterOptions([])
    }, [columns])

    const rows = useMemo(() => {
        if (!domainData) return []
        let items = Array.isArray(domainData) ? domainData : [domainData]
        if (search.trim()) {
            const q = search.toLowerCase()
            items = items.filter(item => {
                if (!item || typeof item !== 'object') return false
                return Object.values(item).some(v => formatCellValue(v).toLowerCase().includes(q)) ||
                    Object.keys(item).some(k => k.toLowerCase().includes(q))
            })
        }
        if (sqlFilter.trim()) {
            const parsed = parseWhereClause(sqlFilter)
            if (!parsed) {
                setFilterError('Invalid filter syntax')
                return items
            }
            setFilterError(null)
            items = items.filter(item => {
                if (!item || typeof item !== 'object') return false
                return evaluateWhere(item, parsed)
            })
        } else {
            setFilterError(null)
        }
        return items
    }, [domainData, search, sqlFilter])

    const handleCopy = () => {
        navigator.clipboard.writeText(data).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        })
    }

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa'}}>
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_, v) => v && setViewMode(v)}
                    size="small"
                >
                    <ToggleButton value="friendly" sx={{py: 0.3, px: 1.5, textTransform: 'none', fontSize: '0.8rem'}}>
                        <ViewListIcon sx={{fontSize: 16, mr: 0.5}}/> Friendly
                    </ToggleButton>
                    <ToggleButton value="raw" sx={{py: 0.3, px: 1.5, textTransform: 'none', fontSize: '0.8rem'}}>
                        <CodeIcon sx={{fontSize: 16, mr: 0.5}}/> Raw
                    </ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title={copied ? 'Copied!' : 'Copy JSON to clipboard'}>
                    <IconButton size="small" onClick={handleCopy}>
                        <ContentCopyIcon sx={{fontSize: 16}}/>
                    </IconButton>
                </Tooltip>
            </Box>

            {viewMode === 'raw' && (
                <Box sx={{flex: 1, minHeight: 0}}>
                    <Editor
                        height="100%"
                        language="json"
                        theme="vs-dark"
                        value={data}
                        options={{
                            readOnly: true,
                            minimap: {enabled: true},
                            scrollBeyondLastLine: false,
                            fontSize: 13,
                            wordWrap: 'on',
                            lineNumbers: 'on',
                            folding: true,
                            automaticLayout: true,
                            find: {
                                addExtraSpaceOnTop: true,
                                autoFindInSelection: 'never',
                                seedSearchStringFromSelection: 'always'
                            }
                        }}
                    />
                </Box>
            )}

            {viewMode === 'friendly' && (
                <Box sx={{display: 'flex', flex: 1, minHeight: 0}}>
                    {/* Sidebar */}
                    <Box sx={{
                        width: 220,
                        minWidth: 220,
                        borderRight: '1px solid #e0e0e0',
                        overflow: 'auto',
                        backgroundColor: '#fafafa'
                    }}>
                        <List disablePadding dense>
                            {domains.map(domain => {
                                const items = rawData[domain]
                                const count = Array.isArray(items) ? items.length : 1
                                return (
                                    <ListItemButton
                                        key={domain}
                                        selected={selectedDomain === domain}
                                        onClick={() => {
                                            setSelectedDomain(domain)
                                            setSearch('')
                                            setSqlFilter('')
                                            setFilterError(null)
                                        }}
                                        sx={{
                                            py: 0.75,
                                            borderBottom: '1px solid #f0f0f0',
                                            '&.Mui-selected': {
                                                backgroundColor: '#e3f2fd',
                                                '&:hover': {backgroundColor: '#bbdefb'}
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                    <Typography variant="body2" sx={{fontWeight: 600, fontSize: '0.8rem', flex: 1}}>
                                                        {domain}
                                                    </Typography>
                                                    <Chip label={count} size="small" variant="outlined" sx={{height: 18, fontSize: '0.7rem', minWidth: 28}}/>
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                )
                            })}
                        </List>
                    </Box>

                    {/* Content */}
                    <Box sx={{flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column'}}>
                        {selectedDomain && domainData && (
                            <>
                                <Box sx={{
                                    p: 1,
                                    borderBottom: '1px solid #e0e0e0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    backgroundColor: '#fff',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1
                                }}>
                                    <Typography variant="subtitle2" sx={{fontWeight: 700}}>{selectedDomain}</Typography>
                                    <TextField
                                        size="small"
                                        placeholder="Search..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        sx={{width: 200}}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{fontSize: 16}}/>
                                                </InputAdornment>
                                            ),
                                            sx: {fontSize: '0.8rem', height: 30}
                                        }}
                                    />
                                    <Chip label={`${rows.length} records`} size="small" color="info" variant="outlined" sx={{height: 20, fontSize: '0.7rem'}}/>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto'}}>
                                        <Autocomplete
                                            freeSolo
                                            disableClearable
                                            open={filterOpen && filterOptions.length > 0}
                                            onOpen={() => setFilterOpen(true)}
                                            onClose={() => setFilterOpen(false)}
                                            options={filterOptions}
                                            filterOptions={(x) => x}
                                            inputValue={sqlFilter}
                                            onInputChange={(_, value, reason) => {
                                                if (reason === 'input') {
                                                    setSqlFilter(value)
                                                    const pos = filterInputRef.current?.selectionStart ?? value.length
                                                    updateFilterOptions(value, pos)
                                                    setFilterOpen(true)
                                                }
                                            }}
                                            onChange={(_, value) => {
                                                if (typeof value !== 'string') return
                                                const input = filterInputRef.current
                                                const pos = input?.selectionStart ?? sqlFilter.length
                                                const textBefore = sqlFilter.substring(0, pos)
                                                const textAfter = sqlFilter.substring(pos)
                                                const lastSpace = textBefore.lastIndexOf(' ')
                                                const newText = textBefore.substring(0, lastSpace + 1) + value + ' ' + textAfter.trimStart()
                                                setSqlFilter(newText)
                                                setTimeout(() => {
                                                    const newPos = (textBefore.substring(0, lastSpace + 1) + value + ' ').length
                                                    input?.setSelectionRange(newPos, newPos)
                                                    input?.focus()
                                                    updateFilterOptions(newText, newPos)
                                                    setFilterOpen(true)
                                                }, 0)
                                            }}
                                            PopperComponent={(props) => (
                                                <Popper {...props} style={{...props.style, width: 220}} placement="bottom-start" />
                                            )}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option} style={{fontSize: '0.8rem', fontFamily: 'monospace', padding: '4px 12px'}}>
                                                    {option}
                                                </li>
                                            )}
                                            sx={{width: 350}}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    inputRef={filterInputRef}
                                                    size="small"
                                                    placeholder="WHERE field = value AND ..."
                                                    error={!!filterError}
                                                    helperText={filterError}
                                                    sx={{'& .MuiFormHelperText-root': {position: 'absolute', bottom: -16, fontSize: '0.65rem'}}}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <FilterListIcon sx={{fontSize: 16, color: sqlFilter ? '#1976d2' : undefined}}/>
                                                            </InputAdornment>
                                                        ),
                                                        sx: {fontSize: '0.8rem', height: 30, fontFamily: 'monospace'}
                                                    }}
                                                    onFocus={() => {
                                                        updateFilterOptions(sqlFilter, filterInputRef.current?.selectionStart ?? sqlFilter.length)
                                                        setFilterOpen(true)
                                                    }}
                                                    onClick={() => {
                                                        updateFilterOptions(sqlFilter, filterInputRef.current?.selectionStart ?? sqlFilter.length)
                                                        setFilterOpen(true)
                                                    }}
                                                />
                                            )}
                                        />
                                        <Tooltip title={
                                            <Box sx={{fontSize: '0.75rem', lineHeight: 1.6, p: 0.5}}>
                                                <b>SQL-style filter syntax:</b><br/>
                                                field = value<br/>
                                                field != value<br/>
                                                field {'>'} value &nbsp;/&nbsp; field {'<'} value<br/>
                                                field {'>'}= value &nbsp;/&nbsp; field {'<'}= value<br/>
                                                field LIKE %pattern%<br/>
                                                field NOT LIKE %pattern%<br/>
                                                field IS NULL / field IS NOT NULL<br/>
                                                field IN ('a','b','c')<br/>
                                                Combine with AND / OR<br/>
                                                <i>Example: State = 'TX' AND Premium {'>'} 100</i>
                                            </Box>
                                        } arrow placement="bottom-end">
                                            <HelpOutlineIcon sx={{fontSize: 16, color: '#999', cursor: 'pointer'}}/>
                                        </Tooltip>
                                    </Box>
                                </Box>
                                <Box sx={{flex: 1, overflow: 'auto'}}>
                                    {Array.isArray(domainData) && columns.length > 0 ? (
                                        <TableContainer>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {columns.map(col => (
                                                            <TableCell key={col} sx={{fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap', backgroundColor: '#f5f5f5'}}>
                                                                {col}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {rows.map((row: any, idx: number) => (
                                                        <TableRow key={idx} hover>
                                                            {columns.map(col => (
                                                                <TableCell key={col} sx={{fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap'}}>
                                                                    {formatCellValue(row?.[col])}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Box sx={{p: 2}}>
                                            <Editor
                                                height="400px"
                                                language="json"
                                                theme="vs-dark"
                                                value={JSON.stringify(sortKeys(domainData), null, 2)}
                                                options={{
                                                    readOnly: true,
                                                    minimap: {enabled: false},
                                                    scrollBeyondLastLine: false,
                                                    fontSize: 13,
                                                    wordWrap: 'on',
                                                    folding: true,
                                                    automaticLayout: true
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}
                        {!selectedDomain && (
                            <Box sx={{p: 2, color: '#888', fontStyle: 'italic'}}>Select a domain from the sidebar</Box>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    )
}

export default ProposalJsonViewer
